import type { ChartDataItem, BarChartConfig } from '../types';
import { TOP_RADIUS, BOTTOM_RADIUS, BOTH_RADIUS } from '../shared/barRadius';
import { resolveItemOpacity } from '../shared/selection';
import { buildMarkLine } from './markLine';

// Matches amcharts4: cellStartLocation=0.2, cellEndLocation=0.8 → 20% gap each side
// amcharts4: cellStart=0.2/cellEnd=0.8 (60% slot) × width=85% ≈ 51% of bandwidth → ~50% gap
const BAR_CATEGORY_GAP = '50%';

export const buildBarSeries = (
  data: ChartDataItem[],
  orderedFields: string[],
  categoryField: string,
  config: BarChartConfig,
) => {
  const {
    theme,
    stacked,
    selectedIndex,
    monthlyAverage,
    alwaysShowTargetLine,
    formatAmount,
    privacyMask,
    colorOverrides,
  } = config;
  const showTargetLine = monthlyAverage !== undefined && (alwaysShowTargetLine || monthlyAverage !== 0);

  // For stacked charts, pre-compute which series index is the visual top and bottom
  // per data point. Matches amcharts4's cornerRadiusLastSeries adapter which dynamically
  // finds the last non-zero series rather than assuming last by field index.
  const topSeriesPerPoint = new Array<number>(data.length).fill(-1);
  const bottomSeriesPerPoint = new Array<number>(data.length).fill(-1);

  if (stacked) {
    data.forEach((row, dataIndex) => {
      orderedFields.forEach((field, fieldIndex) => {
        const value = row[field];
        if (typeof value === 'number') {
          if (value > 0) topSeriesPerPoint[dataIndex] = fieldIndex;
          if (value < 0 && bottomSeriesPerPoint[dataIndex] === -1) bottomSeriesPerPoint[dataIndex] = fieldIndex;
        }
      });
    });
  }

  return orderedFields.map((field, idx) => {
    const colors = colorOverrides ?? theme.colors;
    const seriesColor = colors[idx % colors.length];

    const seriesData = data.map((row, dataIndex) => {
      const raw = row[field];
      const value = typeof raw === 'number' ? raw : 0;
      const opacity = resolveItemOpacity(dataIndex, selectedIndex);

      let borderRadius: number | number[];
      if (stacked) {
        const isTop = topSeriesPerPoint[dataIndex] === idx;
        const isBottom = bottomSeriesPerPoint[dataIndex] === idx;
        if (isTop && isBottom) borderRadius = BOTH_RADIUS;
        else if (isTop) borderRadius = TOP_RADIUS;
        else if (isBottom) borderRadius = BOTTOM_RADIUS;
        else borderRadius = 0;
      } else {
        borderRadius = value >= 0 ? TOP_RADIUS : BOTTOM_RADIUS;
      }

      return { value, itemStyle: { opacity, borderRadius, color: seriesColor } };
    });

    const series: Record<string, unknown> = {
      name: field,
      type: 'bar',
      stack: stacked ? 'total' : undefined,
      data: seriesData,
      emphasis: { disabled: true as const },
      barCategoryGap: BAR_CATEGORY_GAP,
    };

    if (idx === 0 && showTargetLine) {
      series.markLine = buildMarkLine(monthlyAverage as number, theme, formatAmount, privacyMask);
    }

    return series;
  });
};
